import { createEffect, createSignal, on, onCleanup } from "solid-js";

export const SCROLL_FADE_SIZE_PX = 40;
const MIN_OPACITY = 0.2;

/**
 * An element's laid-out height, now and whenever it changes. What something pinned at a
 * scrollport's edge covers is the padding, type scale and borders of everything inside it, so it
 * is measured rather than worked out by hand: `pinnedStart` and any scroll inset read the one
 * signal, and both stay right when a component inside the pinned box moves.
 */
export function createMeasuredHeight(getElement: () => HTMLElement | undefined) {
  const [height, setHeight] = createSignal(0);

  createEffect(() => {
    const element = getElement();
    if (!element) return;

    // The border box, not the default content box: padding and borders are most of what a pinned
    // header is, and a content-box observer sleeps through every change to them.
    const observer = new ResizeObserver(() => setHeight(element.offsetHeight));
    observer.observe(element, { box: "border-box" });
    onCleanup(() => observer.disconnect());
  });

  return height;
}

type ScrollFadeOptions = {
  /** Which edges to fade: "top", "bottom", "left", "right" */
  positions?: ("top" | "bottom" | "left" | "right")[];
  /** How much of the scrollport's start edge (its top, or its bottom in a column-reverse list) is
   *  covered by something pinned there. A sticky header is inside the mask like everything else
   *  and would fade with the content passing behind it, so the band begins past it instead. An
   *  accessor for a height that is measured rather than known, re-read whenever it changes. */
  pinnedStart?: number | (() => number);
  /** How deep each fade runs, in px. A short scrollport wants a shallower one, or the fade covers
   *  most of what it shows. */
  size?: number;
};

/**
 * Creates a scroll fade effect on a scrollable element.
 * Adds gradient masks to edges to indicate more content is available.
 */
export function createScrollFade(
  getElement: () => HTMLElement | undefined,
  options: ScrollFadeOptions = {},
) {
  const positions = options.positions ?? ["top", "bottom"];
  const size = options.size ?? SCROLL_FADE_SIZE_PX;
  const pinnedStart = () =>
    typeof options.pinnedStart === "function" ? options.pinnedStart() : (options.pinnedStart ?? 0);
  const [isFadeShown, setIsFadeShown] = createSignal(false);

  const getOpacity = (scroll: number, maxScroll: number) => {
    return Math.max(1 - scroll / (maxScroll || 1), MIN_OPACITY);
  };

  const updateScrollFade = () => {
    const element = getElement();
    if (!element) {
      setIsFadeShown(false);
      return;
    }

    const { scrollHeight, clientHeight, scrollWidth, clientWidth } = element;
    const maxVerticalScroll = scrollHeight - clientHeight;
    const maxHorizontalScroll = scrollWidth - clientWidth;

    const hasHorizontalScroll = maxHorizontalScroll > 1;
    const hasVerticalScroll = maxVerticalScroll > 1;

    const reversed = getComputedStyle(element).flexDirection === "column-reverse";
    const scrollTop = reversed ? -1 * element.scrollTop : element.scrollTop;
    const scrollLeft = element.scrollLeft;

    const topOpacity =
      positions.includes("top") && hasVerticalScroll ? getOpacity(scrollTop, maxVerticalScroll) : 1;

    const bottomOpacity =
      positions.includes("bottom") && hasVerticalScroll
        ? getOpacity(maxVerticalScroll - scrollTop, maxVerticalScroll)
        : 1;

    const leftOpacity =
      positions.includes("left") && hasHorizontalScroll
        ? getOpacity(scrollLeft, maxHorizontalScroll)
        : 1;

    const rightOpacity =
      positions.includes("right") && hasHorizontalScroll
        ? getOpacity(maxHorizontalScroll - scrollLeft, maxHorizontalScroll)
        : 1;

    if (hasHorizontalScroll || hasVerticalScroll) {
      // Fixes an issue where mask-image renders the gradient instead of using it as a mask
      element.style.transform = "translate3d(0, 0, 0)";
    }

    const topFadeVisible = positions.includes("top") && hasVerticalScroll && topOpacity < 1;
    const bottomFadeVisible =
      positions.includes("bottom") && hasVerticalScroll && bottomOpacity < 1;
    const leftFadeVisible = positions.includes("left") && hasHorizontalScroll && leftOpacity < 1;
    const rightFadeVisible = positions.includes("right") && hasHorizontalScroll && rightOpacity < 1;

    setIsFadeShown(topFadeVisible || bottomFadeVisible || leftFadeVisible || rightFadeVisible);

    const masks: string[] = [];

    if (hasVerticalScroll) {
      const pinned = pinnedStart();
      const start =
        pinned > 0
          ? `black ${pinned}px, rgba(0, 0, 0, ${topOpacity}) ${pinned}px`
          : `rgba(0, 0, 0, ${topOpacity})`;

      masks.push(`linear-gradient(
        to ${reversed ? "top" : "bottom"},
        ${start},
        black ${pinned + size}px,
        black calc(100% - ${size}px),
        rgba(0, 0, 0, ${bottomOpacity})
      )`);
    }

    if (hasHorizontalScroll) {
      masks.push(`linear-gradient(
        to right,
        rgba(0, 0, 0, ${leftOpacity}),
        black ${size}px,
        black calc(100% - ${size}px),
        rgba(0, 0, 0, ${rightOpacity})
      )`);
    }

    element.style.maskImage = masks.join(", ");
  };

  // Set up listeners when element is available
  let resizeObserver: ResizeObserver | null = null;
  let mutationObserver: MutationObserver | null = null;

  const setup = () => {
    const element = getElement();
    if (!element) return;

    updateScrollFade();
    element.addEventListener("scroll", updateScrollFade);

    resizeObserver = new ResizeObserver(updateScrollFade);
    resizeObserver.observe(element);

    // The scrollport's own box is not its content: a panel unfolding inside it grows scrollHeight
    // without resizing the element, and nothing above would notice until the next scroll. So the
    // children are observed too, and children added later are picked up as they arrive.
    const observeChildren = () => {
      for (const child of element.children) resizeObserver?.observe(child);
    };
    observeChildren();
    mutationObserver = new MutationObserver(() => {
      observeChildren();
      updateScrollFade();
    });
    mutationObserver.observe(element, { childList: true });
  };

  // Use queueMicrotask to ensure element is mounted
  queueMicrotask(setup);

  // A measured pinned height lands after the first paint, and can change with it.
  if (typeof options.pinnedStart === "function") {
    createEffect(on(pinnedStart, updateScrollFade, { defer: true }));
  }

  onCleanup(() => {
    const element = getElement();
    if (element) {
      element.removeEventListener("scroll", updateScrollFade);
    }
    resizeObserver?.disconnect();
    mutationObserver?.disconnect();
  });

  return isFadeShown;
}
